'use client'

import React, { useMemo, useState, useRef } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Upload } from 'lucide-react'
import { contactInfoSchema } from '../schemas'

interface ContactInfoStepProps {
  formData: {
    contactPhone: string
    contactEmail: string
  }
  selectedFiles: File[]
  onChange: (field: string, value: any) => void
  onFileChange: (files: File[]) => void
  onRemoveFile: (index: number) => void
  onNext: () => void
  onPrevious: () => void
  loading?: boolean
  clientContactInfo?: { contact_phone: string | null; email: string | null } | null
}

const normalizePhoneNumber = (raw: string) => {
  const value = raw.replace(/[^0-9]/g, '')
  if (value.length <= 3) return value
  if (value.length <= 7) return `${value.slice(0, 3)}-${value.slice(3)}`
  return `${value.slice(0, 3)}-${value.slice(3, 7)}-${value.slice(7, 11)}`
}

export const ContactInfoStep: React.FC<ContactInfoStepProps> = ({
  formData,
  selectedFiles,
  onChange,
  onFileChange,
  onRemoveFile,
  onNext,
  onPrevious,
  loading = false,
  clientContactInfo = null,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [fileError, setFileError] = useState<string | null>(null)
  const [forceEditContact, setForceEditContact] = useState(false)
  const phoneInputRef = useRef<HTMLInputElement>(null)
  const emailInputRef = useRef<HTMLInputElement>(null)

  const isPhoneValid = useMemo(() => {
    return contactInfoSchema.pick({ contactPhone: true }).safeParse({ contactPhone: formData.contactPhone }).success
  }, [formData.contactPhone])

  const isEmailValid = useMemo(() => {
    return contactInfoSchema.pick({ contactEmail: true }).safeParse({ contactEmail: formData.contactEmail }).success
  }, [formData.contactEmail])

  // 클라이언트 정보가 있고, 현재 값이 유효할 때만 필드를 숨김
  const hasClientPhone = Boolean(clientContactInfo?.contact_phone) && isPhoneValid
  const hasClientEmail = Boolean(clientContactInfo?.email) && isEmailValid
  // 연락처 형식이 유효하지 않으면 항상 입력 필드 표시
  const shouldShowContactInputs = !hasClientPhone || !hasClientEmail || forceEditContact

  const canProceed = useMemo(() => {
    return contactInfoSchema.safeParse(formData).success
  }, [formData])

  const phoneFormatHint = useMemo(() => {
    if (!formData.contactPhone) return ''
    return /^010-\d{3,4}-\d{4}$/.test(formData.contactPhone) ? '' : '휴대폰 번호 형식을 확인해주세요. 예) 010-1234-5678'
  }, [formData.contactPhone])

  const clearFieldError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev
      const next = { ...prev }
      delete next[field]
      return next
    })
  }

  const handleNext = () => {
    const result = contactInfoSchema.safeParse(formData)

    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors
      const simplified: Record<string, string> = {}

      Object.keys(fieldErrors).forEach((key) => {
        const arr = fieldErrors[key as keyof typeof fieldErrors]
        if (arr?.[0]) simplified[key] = arr[0]
      })

      setErrors(simplified)

      // 첫 번째 에러 필드로 포커스 및 스크롤
      if (simplified.contactPhone && phoneInputRef.current) {
        phoneInputRef.current.focus()
        phoneInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      } else if (simplified.contactEmail && emailInputRef.current) {
        emailInputRef.current.focus()
        emailInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }

      return
    }

    setErrors({})
    onNext()
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return

    const files = Array.from(e.target.files)
    const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
    const MAX_FILE_COUNT = 10

    // 파일 크기 검증
    const validFiles = files.filter((file) => file.size <= MAX_FILE_SIZE)
    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE)

    const duplicatedFiles = validFiles.filter((file) =>
      selectedFiles.some((existing) => existing.name === file.name && existing.size === file.size)
    )
    const uniqueFiles = validFiles.filter(
      (file) => !selectedFiles.some((existing) => existing.name === file.name && existing.size === file.size)
    )

    const remainingSlots = Math.max(0, MAX_FILE_COUNT - selectedFiles.length)
    const filesToAdd = uniqueFiles.slice(0, remainingSlots)

    const messages: string[] = []

    if (oversizedFiles.length > 0) {
      messages.push(`10MB 초과 파일 ${oversizedFiles.length}개는 제외되었습니다.`)
    }
    if (duplicatedFiles.length > 0) {
      messages.push(`중복 파일 ${duplicatedFiles.length}개는 제외되었습니다.`)
    }
    if (uniqueFiles.length > remainingSlots) {
      messages.push(`최대 ${MAX_FILE_COUNT}개까지만 첨부할 수 있습니다.`)
    }

    setFileError(messages.length > 0 ? messages.join(' ') : null)

    // 유효한 파일들만 추가
    if (filesToAdd.length > 0) {
      // 기존 파일과 새 파일 합치기
      const allFiles = [...selectedFiles, ...filesToAdd]
      onFileChange(allFiles)
    }

    e.target.value = ''
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">추가 정보</h2>
        <p className="text-sm text-gray-600 leading-relaxed">
          정확한 상담을 위해 추가 정보를 입력해주세요. 선택사항이지만, 입력하면 원활한 상담이 가능합니다.
        </p>
      </div>

      <section className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
        {Boolean(clientContactInfo?.contact_phone || clientContactInfo?.email) && (!hasClientPhone || !hasClientEmail) && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs text-amber-700">
              등록된 연락처 형식을 확인할 수 없어 수정 입력이 필요합니다. 아래 필드에서 올바른 형식으로 입력해주세요.
            </p>
          </div>
        )}

        {!shouldShowContactInputs && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-gray-500">기본 연락처가 자동 적용됩니다.</p>
              <button
                type="button"
                onClick={() => setForceEditContact(true)}
                className="text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                연락처 직접 수정
              </button>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-gray-700">전화번호: {normalizePhoneNumber(formData.contactPhone)}</p>
              <p className="text-sm text-gray-700">이메일: {formData.contactEmail}</p>
            </div>
          </div>
        )}

        {/* 연락처 - 클라이언트 정보가 없거나 직접 수정을 선택한 경우 표시 */}
        {shouldShowContactInputs && (
          <div className="space-y-4">
            {forceEditContact && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setForceEditContact(false)
                    if (clientContactInfo?.contact_phone) onChange('contactPhone', normalizePhoneNumber(clientContactInfo.contact_phone))
                    if (clientContactInfo?.email) onChange('contactEmail', clientContactInfo.email)
                    setErrors((prev) => {
                      const next = { ...prev }
                      delete next.contactPhone
                      delete next.contactEmail
                      return next
                    })
                  }}
                  className="text-xs font-medium text-gray-600 hover:text-gray-700"
                >
                  수정 취소
                </button>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 전화번호 - 클라이언트 정보가 없을 때만 표시 */}
              {(!hasClientPhone || forceEditContact) && (
                <div className="space-y-2">
                  <Label htmlFor="contactPhone" className="text-sm font-medium text-gray-700">
                    전화번호 <span className="text-blue-600">필수</span>
                  </Label>
                  <Input
                    ref={phoneInputRef}
                    id="contactPhone"
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => {
                      const formatted = normalizePhoneNumber(e.target.value)
                      onChange('contactPhone', formatted)
                      clearFieldError('contactPhone')
                    }}
                    placeholder="010-1234-5678"
                    maxLength={13}
                    required
                    className={[
                      'h-11 rounded-xl',
                      errors.contactPhone ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : '',
                    ].join(' ')}
                  />
                  {errors.contactPhone && <p className="text-xs text-red-500">{errors.contactPhone}</p>}
                  {!errors.contactPhone && phoneFormatHint && <p className="text-xs text-red-500">{phoneFormatHint}</p>}
                </div>
              )}

              {/* 이메일 - 클라이언트 정보가 없을 때만 표시 */}
              {(!hasClientEmail || forceEditContact) && (
                <div className="space-y-2">
                  <Label htmlFor="contactEmail" className="text-sm font-medium text-gray-700">
                    이메일 <span className="text-blue-600">필수</span>
                  </Label>
                  <Input
                    ref={emailInputRef}
                    id="contactEmail"
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => {
                      onChange('contactEmail', e.target.value)
                      clearFieldError('contactEmail')
                    }}
                    placeholder="contact@example.com"
                    required
                    className={[
                      'h-11 rounded-xl',
                      errors.contactEmail ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : '',
                    ].join(' ')}
                  />
                  {errors.contactEmail && <p className="text-xs text-red-500">{errors.contactEmail}</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 연락처 섹션과 나머지 섹션 사이 구분선 */}
        <div className="border-t border-gray-100" />

        {/* 파일 업로드 */}
        <div className="space-y-3">
          <Label className="text-sm font-medium text-gray-700">
            자료 파일 업로드 <span className="text-xs text-gray-500">(선택)</span>
          </Label>

          <div className="space-y-3">
            <label className="block">
              <input
                type="file"
                multiple
                onChange={handleFileInputChange}
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.zip,.rar,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                disabled={loading}
              />
              <div className="rounded-xl border border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer">
                <div className="px-4 py-4 text-center">
                  <div className="inline-flex items-center gap-2 text-sm font-medium text-blue-600">
                    <Upload className="w-4 h-4" />
                    파일 선택
                  </div>
                  <p className="mt-1 text-xs text-gray-500">여러 개 선택 가능 · 파일당 최대 10MB · 최대 10개</p>
                </div>
              </div>
            </label>

            {fileError && <p className="text-xs text-amber-700">{fileError}</p>}

            {selectedFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500">첨부됨: {selectedFiles.length}/10</p>
                {selectedFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between gap-3 p-3 rounded-xl border border-gray-200 bg-white"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                      <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onRemoveFile(index)}
                      className="text-sm font-medium text-blue-600 hover:text-blue-700"
                      disabled={loading}
                    >
                      제거
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </section>

    </div>
  )
}
